import VideoPlayer from "../components/VideoPlayer";
import { getSinglePodcast, podcastViewed, getAllPodcasts } from "../apiCalls/podcasts/api_v1_podcasts";
import GamezGuides from "../components/GamezGuides";
import {React, useState, useEffect, useCallback} from "react";
import {Box, CircularProgress, Typography, IconButton, Stack, Button} from "@mui/material";
import { useLocation } from "react-router-dom";
import { hasRatedVideo } from "../apiCalls/feedback/feedback";
import RatingDialog from '../components/RatingDialog';
import ShareIcon from '@mui/icons-material/Share';
import StarIcon from '@mui/icons-material/Star';

function Podcast () {
    const [podcast, setPodcast] = useState(null);
    const [loading, setLoading] = useState(false);
    const [ratingDialogOpen, setRatingDialogOpen] = useState(false);
    const [ratingShown, setRatingShown] = useState(false);
    const [videoRef, setVideoRef] = useState(null);
    const [ratingData, setRatingData] = useState({ average: 0, count: 0 });
    const [loadingRating, setLoadingRating] = useState(false);
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const podcastUuid = queryParams.get('uuid');
    const skillLevel = sessionStorage.getItem("skillLevel");

    useEffect(() => {
        document.title = 'Game Guidez | Gamez Academy';
    }, [])

    const checkRatingAt17Percent = async () => {
        const ratingKey = `ratingShown_${podcastUuid}`;
        const alreadyShown = sessionStorage.getItem(ratingKey);
        
        if (ratingShown || alreadyShown) return; // Only show once per podcast
        
        setRatingShown(true);
        sessionStorage.setItem(ratingKey, 'true');
        setRatingDialogOpen(true);
    };

    // Function to fetch rating data from API - wrapped in useCallback
    const fetchRatingData = useCallback(async () => {
        setLoadingRating(true);
        try {
            const response = await getAllPodcasts(podcastUuid);
            if (response.status === 200) {
                setRatingData({
                    average: response.data.average_rating || 0,
                    count: response.data.rating_count || 0
                });
            }
        } catch (error) {
            console.error('Error fetching rating data:', error);
            // Set default values in case of error
            setRatingData({
                average: 0,
                count: 0
            });
        } finally {
            setLoadingRating(false);
        }
    }, [podcastUuid]);

    // Function to handle share button click
    const handleShare = async () => {
        const shareUrl = `${window.location.origin}${window.location.pathname}?uuid=${podcastUuid}`;
        
        if (navigator.share) {
            try {
                await navigator.share({
                    title: podcast.title,
                    text: 'Check out this podcast on Gamez Academy',
                    url: shareUrl,
                });
            } catch (error) {
                console.log('Error sharing:', error);
            }
        } else {
            // Fallback for browsers that don't support Web Share API
            navigator.clipboard.writeText(shareUrl)
                .then(() => {
                    alert('Link copied to clipboard!');
                })
                .catch(err => {
                    console.error('Could not copy text: ', err);
                });
        }
    };

    useEffect(() => {
        const fetchPodcast = async () => {
            setLoading(true);
            console.log(podcastUuid);
            try {
                const response = await podcastViewed(podcastUuid);
                if (response.status === 200) {
                    console.log(response)
                    setPodcast(response.data.data);
                    // Fetch rating data after podcast is loaded
                    fetchRatingData();
                }
            } catch (error) {
                console.error('Error fetching podcast:', error);
            } finally {
                setLoading(false);
            }
        };
        
        // Reset rating state when podcast changes
        setRatingShown(false);
        setRatingDialogOpen(false);
        
        fetchPodcast();
    }, [podcastUuid, fetchRatingData]); // Added fetchRatingData to dependencies

    return (
        <Box
            sx={{
                p: 2,
                color: 'white',
            }}
        >
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                    <CircularProgress />
                </Box>
            ) : podcast ? (
                <>
                    <VideoPlayer
                        videoUrl={podcast.video}
                        onTimeUpdate={(currentTime, duration) => {
                            const watchedPercentage = (currentTime / duration) * 100;
                            const ratingKey = `ratingShown_${podcastUuid}`;
                            const alreadyShown = sessionStorage.getItem(ratingKey);
                            
                            if (watchedPercentage >= 17 && !ratingShown && !alreadyShown) {
                                checkRatingAt17Percent();
                            }
                        }}
                        onVideoRef={(ref) => setVideoRef(ref)}
                    />
                    {podcast.title && (
                        <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            mt: 1,
                            mb: 2,
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: { xs: 2, sm: 0 }
                        }}>
                            <Typography 
                                variant="h6" 
                                sx={{ 
                                    color: 'white', 
                                    fontWeight: 600,
                                    textAlign: 'left',
                                    flex: 1,
                                    mr: 2
                                }}
                            >
                                {podcast.title}
                            </Typography>
                            
                            <Stack direction="row" spacing={2} alignItems="center">
                                {/* Rating display as button */}
                                <Button
                                    variant="contained"
                                    sx={{ 
                                        backgroundColor: '#FFD700',
                                        color: 'black',
                                        '&:hover': {
                                            backgroundColor: '#FFC400',
                                        },
                                        textTransform: 'none',
                                        py: 0.5, // Reduced from py: 1
                                        px: 1.5, // Reduced from px: 2
                                        fontWeight: 600,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        minWidth: '120px',
                                        height: '36px', // Reduced from 40px
                                        justifyContent: 'center'
                                    }}
                                    disabled={loadingRating}
                                >
                                    {loadingRating ? (
                                        <CircularProgress size={16} sx={{ color: 'black' }} />
                                    ) : (
                                        <>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {podcast?.average_rating}
                                            </Typography>
                                            <StarIcon sx={{ color: 'black', fontSize: '18px' }} />
                                            <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                                ({podcast?.total_ratings})
                                            </Typography>
                                        </>
                                    )}
                                </Button>
                                
                                {/* Share button */}
                                <Button
                                    variant="contained"
                                    startIcon={<ShareIcon />}
                                    onClick={handleShare}
                                    sx={{ 
                                        backgroundColor: '#FFD700',
                                        color: 'black',
                                        '&:hover': {
                                            backgroundColor: '#FFC400',
                                        },
                                        textTransform: 'none',
                                        py: 0.5, // Reduced from py: 1
                                        px: 1.5, // Reduced from px: 2
                                        fontWeight: 600,
                                        minWidth: '120px',
                                        height: '36px' // Reduced from 40px
                                    }}
                                >
                                    Share
                                </Button>
                            </Stack>
                        </Box>
                    )}
                </>
            ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                    <CircularProgress />
                </Box>
            )}

            <Box sx={{ 
                width: '100%', 
                height: '1px', 
                backgroundColor: 'rgba(255, 255, 255, 0.2)', 
                my: 3 
            }} />

            <GamezGuides skillLevel={skillLevel}/>
            
            <RatingDialog 
                open={ratingDialogOpen}
                onClose={() => {
                    setRatingDialogOpen(false);
                    if (videoRef && videoRef.paused) {
                        videoRef.play().catch(() => {});
                    }
                    // Refresh rating data after user submits a rating
                    fetchRatingData();
                }}
                onOpen={() => {
                    if (videoRef && !videoRef.paused) {
                        videoRef.pause();
                    }
                }}
                contentId={podcastUuid}
                contentType="podcast"
            />
        </Box>
    );
}

export default Podcast;